import { useCallback, useEffect, useRef, useState } from "react";
import { UserService } from "../services/UserService";
import { User } from "../storage/store";
import { useUser } from "../providers/UserProvider";
import { PLACES_CHANGED } from "../components/places/places";

// Copies each device's last state into device.props, as the pages always have:
// fans keep only state, speed and time; everything else gets the whole entry.
export const withLastState = (devices, lastStates) => {
  const states = lastStates?.[0]?.devices ?? {};
  devices.forEach((device) => {
    const last = states[device.applianceId];
    if (last && "speed" in last) {
      device.props = { state: last.state, speed: last.speed, timeStamp: last.timeStamp };
    } else {
      device.props = { state: last ? last.state : false, timeStamp: last?.timeStamp ?? null, ...last };
    }
  });
  return devices;
};

// Loads the selected property's devices and floors, with each device's last
// state. Reloads when the property changes.
const usePropertyDevices = () => {
  const { setAllDevices, setPropertyOwner } = useUser();
  const propertyId = User.getPropertyId();
  const [result, setResult] = useState({ devices: null, floors: null, error: false });
  const latest = useRef(0);

  const load = useCallback(async () => {
    const request = ++latest.current;
    if (!propertyId) return;
    try {
      const config = await UserService.getConfig(propertyId);
      const lastStates = await UserService.getLastStateByProperty(propertyId);
      if (request !== latest.current) return;
      const devices = withLastState(config?.devices ?? [], lastStates);
      setAllDevices(devices);
      // The + menu offers Share Property only to the property's creator.
      setPropertyOwner({ propertyId, ownedBy: config?.ownedBy ?? null });
      setResult({ devices, floors: config?.floors ?? [], error: false });
    } catch (error) {
      if (request !== latest.current) return;
      setResult({ devices: [], floors: [], error: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  useEffect(() => {
    setResult({ devices: null, floors: null, error: false });
    load();
  }, [load]);

  // A floor or room was added or removed elsewhere on the page.
  useEffect(() => {
    window.addEventListener(PLACES_CHANGED, load);
    return () => window.removeEventListener(PLACES_CHANGED, load);
  }, [load]);

  return { ...result, propertyId, reload: load };
};

export default usePropertyDevices;
